import React, { useState, useCallback, useRef, useEffect } from "react";

const FormManager = ({
  initialValues,
  validationRules,
  onSubmit,
  children,
  isNotTimedValue = [],
  onSetFormData
}) => {
  const [formData, setFormData] = useState(initialValues || {});
  const formDataRef = useRef(initialValues || {});
  const [errors, setErrors] = useState({});
  const [isEditable, setIsEditable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate a single field
  const validateField = useCallback(
    (fieldValue, fieldName) => {
      const formRules = validationRules[fieldName] || [];
      const value =
        isNotTimedValue?.includes(fieldName) || typeof fieldValue !== "string"
          ? fieldValue
          : (fieldValue ?? "")?.trim();

      let error = null;

      for (const rule of formRules) {
        if (rule?.required && !value) {
          error = rule?.message || "that field is required.";
          break;
        }
        if (rule?.pattern && value && !rule.pattern.test(value)) {
          error = rule?.message || "invalid formate.";
          break;
        }
        if (rule?.minLength && value && value?.length <= rule?.minLength) {
          error =
            rule?.message || `value must be greater the ${rule?.minLength}.`;
          break;
        }
        if (rule?.maxLength && value && value?.length > rule?.maxLength) {
          error = rule.message || `Exseed Max Lenth ${rule?.minLength}.`;
          break;
        }
        if (rule?.min && value && Number(value) < rule?.min) {
          error = rule?.message;
          break;
        }
        if (rule?.max && value && Number(value) > rule?.max) {
          error = rule?.message;
          break;
        }
        if (rule?.validation) {
          const isString =
            typeof rule?.validation(value, formDataRef?.current) === "string";
          if (isString) {
            error = isString
              ? rule?.validation(value, formDataRef?.current)
              : rule?.message;
          } else {
            error = rule?.validation(value, formDataRef?.current)
              ? ""
              : rule?.message;
          }
          break;
        }
      }

      setErrors((prev) => ({ ...prev, [fieldName]: error }));
      return error;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationRules]
  );

  // Validate all fields
  const validateForm = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    for (const fieldName in validationRules) {
      const error = validateField(formData[fieldName] || "", fieldName);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, validationRules, validateField]);

  // Handle input change
  const handleChange = (e, fieldName) => {
    const value = e.target.value;
    setFormData((prev) => {
      formDataRef.current = { ...prev, [fieldName]: value };
      return { ...prev, [fieldName]: value };
    });
    // Clear error on change to allow user to fix
    setErrors((prev) => ({ ...prev, [fieldName]: null }));
  };

  // Handle form submission
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (validateForm()) {
  //     onSubmit(formData);
  //   }
  // };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const isValid = validateForm();
    if (!isValid) return;

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
    } catch (error) {
      // Handle API errors here if needed
      console.error("Submission error:");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!!onSetFormData) {
      const data = {
        formData,
        errors,
        setFormData,
        setErrors,
        setIsSubmitting,
        setIsEditable,
        validateForm,
      };
      onSetFormData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSetFormData, formData, errors]);

  return children({
    formData,
    setFormData,
    errors,
    setErrors,
    handleChange,
    handleSubmit,
    isEditable,
    setIsEditable,
    validateField,
    isSubmitting
  });
};

export default FormManager;
